import {
  BooleanFragment,
  BundleItemDetailsFragment,
  CommonHardwareProductDetailsFragment,
  ImageContentFragment,
  ImageFragment,
  ItemRelationsFragment,
  RichTextFragment,
  SingleLineFragment,
  TileChunkItemRelationsFragment,
  TileContentChunkFragment,
  TileItemRelationsFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { AccessoryCrystallizeData } from '@/ui/components/pages/ProductDetail/AccessoryDetail/types/AccessoryCrystallizeData';
import { SmartlockCrystallizeData } from '@/ui/components/pages/ProductDetail/SmartlockDetail/types/SmartlockCrystallizeData';
import { Bundle } from '@/ui/services/BundleService';
import { paths } from '@/constants/paths';
import { removePathPrefix } from '@/ui/utils/localization-helpers/localization';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { DocShapeId } from '@/utils/shapeIds';
import { ClassicPdpTileContent, mapClassicPdpTileContent } from './classic-pdp-tile-content';

// Keys are the component ids of the Crystallize tile shape, so the content
// model and this boundary mapper cannot drift apart.
export type { ClassicPdpUsp } from './classic-pdp-tile-content';

export type ClassicProductDetailTile = {
  subtitle?: SingleLineFragment;
  // The one product the tile renders. The tile never reads the surrounding page,
  // so the same layout works on a product page and on a standalone tile page.
  product?: TileItemRelationsFragment;
  'compatibility-icons'?: ImageContentFragment;
  'show-rating'?: BooleanFragment;
  // Repeatable chunk with one single line `text` per trust line.
  usps?: TileContentChunkFragment;
  // Editorial offers of the tile: a `title`, a `subtitle` and a relation that
  // repeats the id of its chunk. They replace the bundles of the linked
  // product, so one product can be sold with different offers per page.
  'upselling-products'?: TileContentChunkFragment;
  // Optional override for the trail above the grid. Left unset, the trail is
  // derived from the catalogue ancestry of the linked product, which also
  // works on a standalone tile page.
  breadcrumbs?: TileItemRelationsFragment;
};

// The two hardware shapes the classic layout supports. It renders a hardware
// purchase flow, and subscriptions or digital products have no colors,
// cylinders or quantity to select.
export type ClassicPdpProductData = SmartlockCrystallizeData | AccessoryCrystallizeData;

export interface ClassicPdpProductRef {
  path: string;
  shapeIdentifier: string;
}

/** One product the server resolved for a classic tile, keyed by its item path. */
export interface ClassicPdpProductEntry {
  path: string;
  data: ClassicPdpProductData;
}

/**
 * Reads the product the editor linked on the tile. The server resolver and the
 * tile share this accessor, so both agree on which product a tile renders.
 */
export function getClassicPdpProductRef(
  tile: ClassicProductDetailTile,
): ClassicPdpProductRef | null {
  const item = tile.product?.items?.[0];

  if (!item?.path) {
    return null;
  }

  return { path: item.path, shapeIdentifier: item.shape.identifier };
}

/** One product the editor linked under `upselling-products`, flattened at the boundary. */
export interface ClassicPdpUpsellProduct {
  id: string;
  name: string;
  path: string;
  image?: ImageFragment;
  price?: number;
  currency?: string;
}

interface ClassicPdpUpsellBundleItem {
  kind: 'bundle';
  id: string;
  bundle: Bundle;
  // Page of the bundle. Left unset when the bundle has none or is an upgrade
  // bundle, and the card then falls back to the chooser.
  pdpPath?: string;
}

interface ClassicPdpUpsellProductItem {
  // A service (Smart Hosting, a digital product) sells per Smart Lock ID, so
  // its card collects the IDs in a modal instead of opening a product page.
  kind: 'product' | 'service';
  id: string;
  product: ClassicPdpUpsellProduct;
}

export type ClassicPdpUpsellItem = ClassicPdpUpsellBundleItem | ClassicPdpUpsellProductItem;

// Shapes that bind to a Smart Lock ID instead of a colour or a cylinder.
const SERVICE_SHAPE_IDS: ReadonlySet<string> = new Set([
  DocShapeId.subscription,
  DocShapeId.digitalProduct,
]);

/** Headings and offers of the upsell chunk, ready for the section to render. */
export interface ClassicPdpUpsell {
  title?: string;
  subtitle?: string;
  items: ClassicPdpUpsellItem[];
}

export interface ClassicPdpBreadcrumb {
  label: string;
  /** Absent on the trailing crumb, which is the current page. */
  path?: string;
}

/** The variant fields a card renders, as `tileChunkItemRelations` selects them. */
interface UpsellProductVariant {
  isDefault?: boolean | null;
  firstImage?: ImageFragment | null;
  priceVariant?: { price?: number | null; currency?: string | null } | null;
}

/**
 * One item of an upsell relation: everything `itemRelations` selects, plus the
 * variants `tileChunkItemRelations` adds on top. The generated fragment is the
 * intersection of two item unions, and TypeScript narrows such an intersection
 * on `__typename` no further than its first half, so the two are joined here.
 */
type UpsellRelationItem = NonNullable<ItemRelationsFragment['items']>[number] & {
  variants?: readonly UpsellProductVariant[] | null;
};

/**
 * Picks the variant a card shows. The editor links whole products, so
 * Crystallize leaves the relation-level `productVariants` list empty and the
 * image and the price come from the variants of the product itself. The
 * default variant wins; the first one only fills a product that has none.
 */
function defaultVariantOf(
  variants: readonly UpsellProductVariant[] | null | undefined,
): UpsellProductVariant | undefined {
  return variants?.find((variant) => variant.isDefault) ?? variants?.[0];
}

/**
 * Flattens a linked product onto the fields the card renders. The chosen
 * variant carries the image and the price the visitor sees before they open
 * the product page and pick a colour or a cylinder.
 */
function mapUpsellProduct(
  item: { id: string; name?: string | null },
  path: string,
  variant: UpsellProductVariant | undefined,
  kind: 'product' | 'service',
): ClassicPdpUpsellItem | null {
  const name = item.name?.trim();

  // A card without a name has no heading and no accessible action label.
  if (!name) {
    return null;
  }

  return {
    kind,
    id: item.id,
    product: {
      id: item.id,
      name,
      path,
      image: variant?.firstImage ?? undefined,
      price: variant?.priceVariant?.price ?? undefined,
      currency: variant?.priceVariant?.currency ?? undefined,
    },
  };
}

/**
 * Builds the upsell offers in the order the editor linked them. Bundles become
 * bundle cards, services become service cards, other products become product
 * cards, and everything else (folders, documents without a product payload)
 * has no card layout.
 */
function mapUpsellItems(
  relation: TileChunkItemRelationsFragment | undefined,
  priceVariantId?: string,
): ClassicPdpUpsellItem[] {
  const items = (relation?.items ?? []) as readonly UpsellRelationItem[];

  return items.reduce<ClassicPdpUpsellItem[]>((result, item) => {
    // Every card links its item, so an item without a path has no target.
    if (!item.path) {
      return result;
    }

    if (item.shape.identifier === DocShapeId.bundle) {
      const bundle = new Bundle(item as unknown as BundleItemDetailsFragment, priceVariantId);

      return [
        ...result,
        {
          kind: 'bundle',
          id: item.id,
          bundle,
          pdpPath: bundle.getLinkablePagePath(),
        },
      ];
    }

    if (item.__typename !== 'Product') {
      return result;
    }

    const kind = SERVICE_SHAPE_IDS.has(item.shape.identifier) ? 'service' : 'product';
    const product = mapUpsellProduct(item, item.path, defaultVariantOf(item.variants), kind);

    return product ? [...result, product] : result;
  }, []);
}

/**
 * Reads the upsell chunk of the tile. The editor maintains the two headings
 * next to the products they introduce, so the section takes both from the CMS
 * instead of borrowing the bundle heading of the linked product.
 */
export function mapClassicPdpUpsell(
  tile: ClassicProductDetailTile,
  priceVariantId?: string,
): ClassicPdpUpsell {
  // The chunk is not repeatable, so the tile has one offer group at most.
  const chunk = tile['upselling-products']?.chunks?.[0];

  return {
    title:
      extractComponentFromChunk(chunk, 'title', 'SingleLineContent')?.text?.trim() || undefined,
    subtitle:
      extractComponentFromChunk(chunk, 'subtitle', 'SingleLineContent')?.text?.trim() || undefined,
    items: mapUpsellItems(
      extractComponentFromChunk(chunk, 'upselling-products', 'ItemRelationsContent'),
      priceVariantId,
    ),
  };
}

/**
 * One catalogue level above the product. GraphQL has no recursion, so the
 * generated type stops at the depth the query spells out; this shape describes
 * the same data and lets one walk read whatever depth came back.
 */
interface ProductAncestor {
  name?: string | null;
  path?: string | null;
  nameComponent?: { content?: { __typename: string; text?: string | null } | null } | null;
  parent?: ProductAncestor | null;
}

/**
 * Label of a crumb. The `name` component holds the editorial label; the item
 * name is the internal one and only serves as a fallback.
 */
function crumbLabel(
  editorialLabel: string | null | undefined,
  name: string | null | undefined,
): string | undefined {
  return editorialLabel?.trim() || name?.trim() || undefined;
}

/** Trail the editor linked on the tile, which overrides the derived one. */
function linkedCrumbs(tile: ClassicProductDetailTile): ClassicPdpBreadcrumb[] {
  return (tile.breadcrumbs?.items ?? []).reduce<ClassicPdpBreadcrumb[]>((result, item) => {
    const label = crumbLabel(
      componentByTypename(item.nameComponent?.content, 'SingleLineContent')?.text,
      item.name,
    );

    if (!item.path || !label) {
      return result;
    }

    return [...result, { label, path: item.path }];
  }, []);
}

/**
 * Walks the catalogue ancestry of the product, nearest ancestor first, and
 * turns it into a trail that reads top down. The hierarchy prefix and the
 * catalogue root collapse to `/` once the prefix is stripped, and neither has
 * a page of its own, so both drop out.
 */
function derivedCrumbs(data: CommonHardwareProductDetailsFragment): ClassicPdpBreadcrumb[] {
  const crumbs: ClassicPdpBreadcrumb[] = [];
  let ancestor = (data as { parent?: ProductAncestor | null }).parent;

  while (ancestor) {
    // The ancestry selects `...singleLine`, so any other content type that a
    // folder carries under `name` arrives with its typename only.
    const content = ancestor.nameComponent?.content;
    const label = crumbLabel(
      content?.__typename === 'SingleLineContent' ? content.text : undefined,
      ancestor.name,
    );

    if (ancestor.path && removePathPrefix(ancestor.path) !== '/' && label) {
      crumbs.unshift({ label, path: ancestor.path });
    }

    ancestor = ancestor.parent;
  }

  return crumbs;
}

/**
 * Crumbs read as titles, and a catalogue folder carries a lowercase internal
 * name ("products") whenever its editorial name component is empty.
 */
function capitalizeCrumb(crumb: ClassicPdpBreadcrumb): ClassicPdpBreadcrumb {
  return { ...crumb, label: crumb.label.charAt(0).toUpperCase() + crumb.label.slice(1) };
}

// Catalogue folders without a page of their own point to the page that stands
// in for them: the `products` folder is a category shape that no route renders,
// so its own path would open a 404. Keys are prefix-stripped catalogue paths.
const FOLDER_PAGE_PATHS: Record<string, string> = {
  '/products': paths.productOverviewMainPage,
};

/** Swaps a folder path for the page that represents the folder. */
function resolveCrumbPath(crumb: ClassicPdpBreadcrumb): ClassicPdpBreadcrumb {
  if (!crumb.path) {
    return crumb;
  }

  const pagePath = FOLDER_PAGE_PATHS[removePathPrefix(crumb.path)];

  return pagePath ? { ...crumb, path: pagePath } : crumb;
}

/**
 * Builds the breadcrumb trail of the tile and appends the product title as the
 * current page. The trail comes from the catalogue ancestry of the product, so
 * it needs no editorial upkeep, and it opens on the home page because the
 * catalogue root is not a crumb of its own. A `breadcrumbs` relation on the
 * tile overrides the whole trail, which is how a campaign page shows a trail
 * the product tree has not got. An empty trail renders nothing, because a lone
 * product title is no trail. A bundle has no catalogue ancestry of its own
 * (`data` is null), so its trail opens on the home page directly.
 */
export function mapClassicPdpBreadcrumbs(
  tile: ClassicProductDetailTile,
  data: CommonHardwareProductDetailsFragment | null,
  productTitle: string,
  homeLabel: string,
): ClassicPdpBreadcrumb[] {
  const linked = linkedCrumbs(tile);

  // An editor can link the folder too, so both trails run through the same
  // folder-to-page lookup.
  if (linked.length > 0) {
    return [...linked, { label: productTitle }].map(capitalizeCrumb).map(resolveCrumbPath);
  }

  const derived = data ? derivedCrumbs(data) : [];

  if (data && derived.length === 0) {
    return [];
  }

  // Only the derived trail gets the home crumb: an editor who links a trail
  // decides for themselves what it opens with.
  return [{ label: homeLabel, path: paths.home }, ...derived, { label: productTitle }]
    .map(capitalizeCrumb)
    .map(resolveCrumbPath);
}

export interface ClassicPdpContent extends ClassicPdpTileContent {
  title: string;
  shortDescription?: RichTextFragment;
}

// Only the product fields the layout needs, so the mapper stays testable
// without constructing a full product service instance.
export interface ClassicPdpProductInput {
  name: string;
}

/**
 * Maps the product payload of the page and the editable tile components onto
 * the flat contract the classic section renders, so no component below this
 * boundary has to know about component ids or content typenames.
 */
export function mapClassicPdpContent(
  data: CommonHardwareProductDetailsFragment,
  tile: ClassicProductDetailTile,
  product: ClassicPdpProductInput,
): ClassicPdpContent {
  const cmsTitle = componentByTypename(data.title?.content, 'SingleLineContent')?.text;

  return {
    ...mapClassicPdpTileContent(tile),
    title: cmsTitle || product.name,
    shortDescription:
      componentByTypename(data.shortDescription?.content, 'RichTextContent') ?? undefined,
  };
}
